import React, { useState, useEffect } from "react";
import { Button, Checkbox, FormControlLabel, Grid, IconButton, InputAdornment, Menu, MenuItem, Stack, TablePagination, TextField, Tooltip, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import { mdiAccountOutline, mdiBriefcaseAccountOutline, mdiCalendarWeekBeginOutline, mdiClose, mdiDotsVertical, mdiExportVariant, mdiHistory, mdiMagnify, mdiPencilOutline, mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import TaskDetailsDialog from "./components/TaskDetailsDialog";
import { useNavigate } from "react-router-dom";
import useAxios from "../../hooks/useAxios";
import { useLoader } from "../../components/context/LoaderContext";
import { useSnackbar } from "../../components/context/SnackbarContext";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import ExportData from "../../components/ExportData";
import { capitalize, DeleteDialog, DeleteDialogConfig } from "../../utils/Utils";
import EmptyState from "../../components/common/EmptyState";

function Tasks(props) {
    const navigate = useNavigate();
    const { showLoader, hideLoader, loading } = useLoader();
    const [checkedItems, setCheckedItems] = useState({});
    const [isCheckAll, setIsCheckAll] = useState(false);
    const [tasks, setTasks] = useState();
    const axiosInstance = useAxios();
    const [filterData, setFilterData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const { preferredDateFormat, preTimeZoneKey } = useSelector((state) => state?.user);
    const userPermissions = useSelector((state) => state.user.permission);
    const [perPage, setPerPage] = useState(parseInt(process.env.REACT_APP_DEFAULT_PAGINATION));
    const { showSnackbar } = useSnackbar();
    const [priorityList, setPriorityList] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [reloadPage, setReloadPage] = useState(false);
    const [search, setSearch] = useState('');
    const [searchFilter, setSearchFilter] = useState('all');
    const [searchPriority, setSearchPriority] = useState('All Priority');
    const [searchStatus, setSearchStatus] = useState('All Status');
    const [taskID, setTaskID] = useState(null);
    const [clearType, setClearType] = useState(false);

    useEffect(() => {
        async function fetchData() {
            showLoader();
            await getData(
                currentPage,
                perPage,
                search,
                searchFilter,
                searchPriority,
                searchStatus
            );
        }
        fetchData();
    }, [
        currentPage,
        perPage,
        searchFilter,
        searchPriority,
        reloadPage,
        searchStatus,
        clearType,
    ]);


    const getData = async (
        currentPage,
        perPage,
        search,
        searchFilter,
        searchPriority,
        searchStatus
    ) => {
        const params = {
            search: search,
            search_filter: searchFilter === "all" ? "" : searchFilter,
            search_priority: searchPriority === "All Priority" ? "" : searchPriority,
            search_activity_status: searchStatus === "All Status" ? "" : searchStatus,
            per_page: perPage,
        };

        await axiosInstance
            .get(`api/task?page=${currentPage + 1}`, { params }).then((response) => {
                if (response.status === 200) {
                    setTasks(response?.data?.data?.records?.data);
                    setTotalCount(response?.data?.data?.records?.total);
                    setFilterData(response?.data?.data?.filterData || []);
                    setPriorityList(response?.data?.data?.priority || []);
                    setStatusList(response?.data?.data?.priorityStatus);
                    hideLoader();
                    setCheckedItems({});
                    setIsCheckAll(false);
                }
            })
            .then((json) => { })
            .catch((error) => {
                if (error.response) {
                    hideLoader();
                    if (error.response.status === 400) {
                        showSnackbar(error.response.data.message, "warning");
                        navigate("/dashboard");
                    }
                }
            });
    };


    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const handleCheckAll = (event) => {
        const { checked } = event.target;
        setIsCheckAll(checked);
        const newCheckedItems = {};
        tasks.forEach((lead) => {
            newCheckedItems[lead.id] = checked;
        });
        setCheckedItems(newCheckedItems);
    };

    const handleCheckboxChange = (event) => {
        const { name, checked } = event.target;
        setCheckedItems({
            ...checkedItems,
            [name]: checked,
        });

        if (!checked) {
            setIsCheckAll(false);
        } else {
            const allChecked = tasks.every(
                (deal) => checkedItems[deal.id] || deal.name === name
            );
            setIsCheckAll(allChecked);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault(); // Prevent the default form submission behavior
            getData(
                currentPage,
                perPage,
                search,
            );
        }
    };

    const getCheckedIds = () => {
        return Object.keys(checkedItems).filter((id) => checkedItems[id]);
    };

    const checkedIds = getCheckedIds();

    const expParams = {
        search: search,
        search_filter: searchFilter === "all" ? "" : searchFilter,
        search_priority: searchPriority === "All Priority" ? "" : searchPriority,
        search_activity_status: searchStatus === "All Status" ? "" : searchStatus,
        per_page: perPage,
        selectedIds: checkedIds && checkedIds.length > 0 ? checkedIds : []
    };

    const handleReloadFunc = () => {
        setCheckedItems({});
        setIsCheckAll(false);
        hideLoader();
        setReloadPage(reloadPage ? false : true);
    }

    const bulkDeleteData = async (ids) => {
        const data = {
            id: ids,
        }
        try {
            DeleteDialog.fire({
                ...DeleteDialogConfig({
                    confirm: async () => {
                        showLoader()
                        await axiosInstance.delete('api/activity-bulk-destroy/', { data }).then((response) => {
                            if (response.status === 200) {
                                showSnackbar(response.data.message, "success");
                                setCheckedItems({});
                                setIsCheckAll(false);
                                hideLoader();
                                setReloadPage(reloadPage ? false : true);
                            }
                        })
                            .then((json) => { })
                            .catch((error) => {
                                if (error.response) {
                                    hideLoader();
                                    if (error.response.status === 400) {
                                        showSnackbar(error.response.data.message, "warning");
                                        navigate("/tasks");
                                    }
                                }
                            });
                    },
                    cancel: () => {
                        hideLoader();
                        setCheckedItems({});
                        setIsCheckAll(false);
                    }
                })
            });
        } catch (error) {
            hideLoader();
            showSnackbar('Oops Something want wrong..!', "error");
        }
    }

    async function handleBulkDelete() {
        const checkedIds = getCheckedIds();
        if (checkedIds && checkedIds.length > 0) {
            bulkDeleteData(checkedIds)
        } else {
            showSnackbar('Please select at least one record to Delete.', 'warning')
        }
    }

    async function deleteData(id) {
        const data = {
            id: id,
        }
        try {
            DeleteDialog.fire({
                ...DeleteDialogConfig({
                    confirm: async () => {
                        showLoader()
                        await axiosInstance.delete(`api/activity/destroy`, { data }).then((response) => {
                            if (response.status === 200) {
                                showSnackbar(response.data.message, "success");
                                hideLoader();
                                setReloadPage(reloadPage ? false : true);
                                navigate("/tasks");
                            }
                        })
                            .then((json) => { })
                            .catch((error) => {
                                if (error.response) {
                                    hideLoader();
                                    if (error.response.status === 400) {
                                        showSnackbar(error.response.data.message, "warning");
                                    }
                                }
                            });
                    },
                    cancel: () => {
                        hideLoader();
                    }
                })
            });
        } catch (error) {
            showSnackbar('Oops Something want wrong..!', "error");
        }
    }

    const updateTaskStatus = async (task, newStatus) => {
        const data = {
            id: task.id,
            activity_status: newStatus,
        };

        try {
            const response = await axiosInstance.post('/api/task/update-action', data);

            if (response.status === 200) {
                showSnackbar(response.data.message, "success");
                hideLoader();
                setReloadPage(reloadPage ? false : true);
            }
        } catch (error) {
            hideLoader();
            if (error.response?.status === 400) {
                showSnackbar(error.response.data.message, "warning");
            } else {
                showSnackbar('Oops, something went wrong!', "error");
            }
        }
    };

    const HandleSearchDue = (event) => {
        setSearchFilter(event.target.value);
    };
    const HandleSearchPriority = (event) => {
        setSearchPriority(event.target.value);
    }
    const HandleSearchStatus = (event) => {
        setSearchStatus(event.target.value);
    }

    const [taskPageContextMenuAnchorEl, setTaskPageContextMenuAnchorEl] = React.useState(null);
    const taskPageContextMenuOpen = Boolean(taskPageContextMenuAnchorEl);
    const handleTaskPageContextMenuClick = (event) => {
        setTaskPageContextMenuAnchorEl(event.currentTarget);
    };
    const handleTaskPageContextMenuClose = () => {
        setTaskPageContextMenuAnchorEl(null);
    };

    //========Start::Write a code for edit task popup=========//
    const [taskDetailsDialogOpen, setTaskDetailsDialogOpen] = React.useState(false);
    const openTaskDetailsDialog = (id) => {
        setTaskID(id)
        setTaskDetailsDialogOpen(true);
    }
    const closeTaskDetailsDialog = () => {
        setTaskID('')
        setTaskDetailsDialogOpen(false);
    }
    //========End::Write a code for edit task popup=========//

    //========Start::Write a code for manage reset filter=========//
    const clearUserFilter = (type) => {
        setClearType(clearType ? false : true)
        if (type === 'search') {
            setSearch('');
        } else if (type === 'due') {
            setSearchFilter('all')
        } else if (type === 'priority') {
            setSearchPriority('All Priority')
        } else if (type === 'status') {
            setSearchStatus('All Status')
        } else {
            setSearch('');
            setSearchFilter('all')
            setSearchPriority('All Priority')
            setSearchStatus('All Status')
        }
    }
    //========End::Write a code for manage reset filter=========//

    const priorityMap = {
        low: { className: 'success', label: 'low' },
        medium: { className: 'warning', label: 'medium' },
        high: { className: 'danger', label: 'high' },
        null: { className: 'info', label: 'N/A' },
    };

    const priorityStatusMap = {
        'not-started': { className: 'danger', label: 'not-started' },
        'in-progress': { className: 'warning', label: 'in-progress' },
        'completed': { className: 'success', label: 'completed' },
        null: { className: 'info', label: 'N/A' },
    }

    return (
        <>
            <Helmet>
                <title>Tasks | {capitalize(props?.name || 'SolvCRM')}</title>
            </Helmet>

            <Grid container className='page-action-bar'>
                <Grid item xs>
                    <Grid container spacing={1} alignItems={'center'}>
                        <Grid item xs={'auto'}>
                            <Checkbox className='grid-checkbox' checked={isCheckAll} onChange={handleCheckAll} />
                        </Grid>
                        <Grid item xs={'auto'}>
                            <TextField
                                variant="filled"
                                placeholder="Search tasks"
                                className='size-small page-search no-margin'
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={handleKeyDown}
                                value={search}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Icon path={mdiMagnify} color="currentColor" size={1} />
                                            {
                                                search &&
                                                <IconButton onClick={() => clearUserFilter('search')} >
                                                    <Icon path={mdiClose} color={'currentColor'} size={'20px'} />
                                                </IconButton>
                                            }

                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={'auto'}>
                            <TextField
                                select
                                //defaultValue="all"
                                variant="filled"
                                className='size-small no-label no-margin'
                                value={searchFilter}
                                onChange={HandleSearchDue}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end' className='clear-filter'>
                                            {
                                                searchFilter && searchFilter !== 'all' &&
                                                <IconButton onClick={() => clearUserFilter('due')} >
                                                    <Icon path={mdiClose} color={'currentColor'} size={'20px'} />
                                                </IconButton>
                                            }

                                        </InputAdornment>
                                    ),
                                }}
                            >
                                {Object.entries(filterData).map(([key, value], index) => (
                                    <MenuItem key={index} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={"auto"}>
                            <Stack direction={"row"} spacing={1} alignItems={"center"}>
                                <TextField
                                    select
                                    variant="filled"
                                    //defaultValue="All Priority"
                                    className='size-small no-label no-margin'
                                    onChange={HandleSearchPriority}
                                    value={searchPriority}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end' className='clear-filter'>
                                                {
                                                    searchPriority && searchPriority !== 'All Priority' &&
                                                    < IconButton onClick={() => clearUserFilter('priority')} >
                                                        <Icon path={mdiClose} color={'currentColor'} size={'20px'} />
                                                    </IconButton>
                                                }
                                            </InputAdornment>
                                        ),
                                    }}
                                >
                                    <MenuItem key={12224} value="All Priority">All Priority</MenuItem>
                                    {Object.entries(priorityList).map(([key, value], index) => (
                                        <MenuItem key={index} value={key}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    select
                                    variant="filled"
                                    className='size-small no-label no-margin'
                                    //defaultValue="All Status"
                                    value={searchStatus}
                                    onChange={HandleSearchStatus}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end' className='clear-filter'>
                                                {
                                                    searchStatus && searchStatus !== 'All Status' &&
                                                    <IconButton onClick={() => clearUserFilter('status')}>
                                                        <Icon path={mdiClose} color={'currentColor'} size={'20px'} />
                                                    </IconButton>
                                                }

                                            </InputAdornment>
                                        ),
                                    }}
                                >
                                    <MenuItem key={1} value='All Status'>All Status</MenuItem>
                                    {Object.entries(statusList).map(([key, value], index) => (
                                        <MenuItem key={index} value={key}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                {
                                    (search || (searchFilter && searchFilter !== 'all') || (searchPriority && searchPriority !== 'All Priority') || (searchStatus && searchStatus !== 'All Status')) &&
                                    <a href="-" className="primary-link ml-12" onClick={e => { e.preventDefault(); clearUserFilter('all') }}><Typography variant='body-medium-medium'>Clear All</Typography></a>
                                }

                            </Stack>
                        </Grid>
                    </Grid>
                </Grid >
                <Grid item xs={'auto'} sx={{ marginLeft: 'auto' }}>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="More">
                            <span>
                                <Button variant="contained" color="secondary" size='small' className='icon-only' onClick={handleTaskPageContextMenuClick}>
                                    <Icon path={mdiDotsVertical} color="currentColor" size={1} />
                                </Button>
                            </span>
                        </Tooltip>
                        <Menu
                            anchorEl={taskPageContextMenuAnchorEl}
                            open={taskPageContextMenuOpen}
                            onClose={handleTaskPageContextMenuClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                            }}
                        >
                            <MenuItem onClick={handleTaskPageContextMenuClose}>
                                <Icon path={mdiExportVariant} color={'currentColor'} size={1} />
                                <ExportData params={expParams} fileName="Tasks" api="task" handleReloadFunc={handleReloadFunc} />
                            </MenuItem>
                            {userPermissions && userPermissions.includes("task_delete") &&
                                <MenuItem onClick={e => {
                                    handleBulkDelete(); handleTaskPageContextMenuClose();
                                }}>
                                    <Icon path={mdiTrashCanOutline} color={'currentColor'} size={1} />
                                    <span>Delete</span>
                                </MenuItem>
                            }
                        </Menu>
                    </Stack>
                </Grid>
            </Grid >

            {tasks && tasks.length > 0 && (
                <ul className="primary-list">
                    {tasks.map((task, index) => {
                        const priorityInfo = priorityMap[task?.priority] || priorityMap['null'];
                        const priorityStatus = priorityStatusMap[task?.activity_status] || priorityStatusMap['null'];
                        const taskDueDate = task?.end_date ? moment(task.end_date) : moment()
                        return (
                            <li key={index + ''}>
                                <a href="-" onClick={e => {
                                    e.preventDefault();
                                    if (userPermissions && userPermissions.includes("task_edit")) {
                                        openTaskDetailsDialog(task?.id);
                                    }
                                }} className="card">
                                    <Grid container alignItems={'flex-start'}>
                                        <Grid item xs={'auto'}>
                                            <Checkbox className='grid-checkbox' onClick={e => { e.stopPropagation(); }}
                                                name={String(task?.id)}
                                                checked={checkedItems[task?.id] || false}
                                                onChange={handleCheckboxChange}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            <Typography variant='title-medium-bold' component={'h6'}>{capitalize(task?.name || 'N/A')}</Typography>
                                            <ul className='inline-details'>
                                                <li>
                                                    <Tooltip title="Contact Name">
                                                        <span>
                                                            <Icon path={mdiBriefcaseAccountOutline} color="currentColor" size={1} /><span tabIndex={0}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    e.preventDefault();
                                                                    if (task?.taskType === 'Lead') {
                                                                        userPermissions && userPermissions.includes("lead_edit") && window.open(`/lead-detail/${task?.lead_id}`, '_blank');
                                                                    } else if (task?.taskType === 'Deal') {
                                                                        userPermissions && userPermissions.includes("deal_edit") && window.open(`/deal-detail/${task?.deal_id}`, '_blank');
                                                                    }
                                                                }}
                                                                className="primary-link dark">{task?.contact_name?.first_name || 'N/A'} {task?.contact_name?.last_name || 'N/A'}
                                                            </span>
                                                        </span>
                                                    </Tooltip>
                                                </li>
                                                <li>
                                                    <Tooltip title="Due Date">
                                                        <span>
                                                            <Icon path={mdiCalendarWeekBeginOutline} color="currentColor" size={1} />
                                                            {task?.end_date
                                                                ? moment.utc(taskDueDate)
                                                                    .tz(preTimeZoneKey)
                                                                    .format(preferredDateFormat)
                                                                : "N/A"}
                                                        </span>
                                                    </Tooltip>
                                                </li>
                                                <li>
                                                    <Tooltip title="Priority">
                                                        <span>
                                                            <span className={`status-badge ${priorityInfo.className}`}>
                                                                {priorityInfo?.label}
                                                            </span>
                                                        </span>
                                                    </Tooltip>
                                                </li>
                                                <li>
                                                    <Tooltip title="Status">
                                                        <span>
                                                            <span className={`status-badge ${priorityStatus.className}`}>
                                                                {priorityStatus?.label}
                                                            </span>
                                                        </span>
                                                    </Tooltip>
                                                </li>
                                            </ul>
                                        </Grid>
                                    </Grid>
                                    <ul className='inline-details other-info no-avatar'>
                                        <li><span className="title">Related to:</span>{task?.relatedToName?.name}</li>
                                        <li>
                                            <Tooltip title="Last Updated">
                                                <span>
                                                    <Icon path={mdiHistory} color="currentColor" size={1} />
                                                    {task?.updated_at
                                                        ? moment.utc(task?.updated_at)
                                                            .tz(preTimeZoneKey)
                                                            .format(preferredDateFormat)
                                                        : "N/A"}

                                                </span>
                                            </Tooltip>
                                        </li>
                                        <li className="before-checkbox">
                                            <Tooltip title="Added by">
                                                <span>
                                                    <Icon path={mdiAccountOutline} color="currentColor" size={1} /> {task?.added_by?.name}
                                                </span>
                                            </Tooltip>
                                        </li>
                                        <li
                                            className="checkbox-wrapper"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const newStatus = task.activity_status === 'completed' ? 'inpro' : 'comp';
                                                updateTaskStatus(task, newStatus);
                                            }}
                                        >
                                            <FormControlLabel
                                                value="Mark as Completed"
                                                control={<Checkbox checked={task.activity_status === 'completed'} />}
                                                label="Mark as Completed"
                                            />
                                        </li>
                                    </ul>
                                </a>
                                <Stack direction={'row'} className='actions'>
                                    {userPermissions && userPermissions.includes("task_edit") &&
                                        <Tooltip title="Edit">
                                            <span>
                                                <button className='icon-button' onClick={() => openTaskDetailsDialog(task?.id)}>
                                                    <Icon path={mdiPencilOutline} color="currentColor" size={1} />
                                                </button>
                                            </span>
                                        </Tooltip>
                                    }
                                    {userPermissions && userPermissions.includes("task_delete") &&
                                        <Tooltip title="Delete">
                                            <span>
                                                <button type="button" className='icon-button' onClick={() => { deleteData(task?.id) }}>
                                                    <Icon path={mdiTrashCanOutline} color="currentColor" size={1} />
                                                </button>
                                            </span>
                                        </Tooltip>
                                    }
                                </Stack>
                            </li>
                        )
                    })
                    }
                </ul>

            )
            }
            {
                (!tasks || tasks.length === 0) && !loading &&
                <EmptyState type={'task'} hideActionBtn />
            }

            {
                tasks && tasks.length > 0 &&
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={currentPage}
                    rowsPerPage={perPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            }

            {
                taskDetailsDialogOpen && taskID &&
                <TaskDetailsDialog
                    open={taskDetailsDialogOpen}
                    onClose={closeTaskDetailsDialog}
                    taskId={taskID}
                    handleReloadFunc={handleReloadFunc}
                    priorityList={priorityList}
                    statusList={statusList}
                />
            }
        </>
    );
}

export default Tasks;